import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en';
import fr from './locales/fr';
import de from './locales/de';
import es from './locales/es';
import it from './locales/it';

export const SUPPORTED_UI_LANGUAGES = ['en', 'fr', 'de', 'es', 'it'] as const;

export type SupportedUiLanguage = (typeof SUPPORTED_UI_LANGUAGES)[number];

export function isSupportedUiLanguage(
  lang: string,
): lang is SupportedUiLanguage {
  return (SUPPORTED_UI_LANGUAGES as readonly string[]).includes(lang);
}

/**
 * Normalise any language tag ("de-AT", "FR") to a supported UI language,
 * or null when unsupported (callers fall back to English).
 */
export function toSupportedUiLanguage(
  tag: string | null | undefined,
): SupportedUiLanguage | null {
  if (!tag) {
    return null;
  }
  const base = tag.toLowerCase().split('-')[0];
  return isSupportedUiLanguage(base) ? base : null;
}

/**
 * Initial language for WA-adjacent public pages: an explicit ?lang= query
 * parameter wins, then the browser language, falling back to English.
 * Token-scoped pages additionally call `applyUserLanguage` once the backend
 * reports the user's preferred language (facts table — the durable source
 * of truth).
 */
export function detectInitialLanguage(): SupportedUiLanguage {
  const params = new URLSearchParams(window.location.search);
  return (
    toSupportedUiLanguage(params.get('lang')) ||
    toSupportedUiLanguage(navigator.language) ||
    'en'
  );
}

/**
 * Switch to the user's preferred language as reported by a backend payload.
 * An explicit ?lang= query parameter still wins (it lets links and humans
 * override), and unsupported languages leave the current fallback in place.
 */
export function applyUserLanguage(language: string | null | undefined): void {
  const params = new URLSearchParams(window.location.search);
  if (toSupportedUiLanguage(params.get('lang'))) {
    return;
  }
  const supported = toSupportedUiLanguage(language);
  if (supported && supported !== i18n.language) {
    i18n.changeLanguage(supported);
  }
}

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    fr: { translation: fr },
    de: { translation: de },
    es: { translation: es },
    it: { translation: it },
  },
  lng: detectInitialLanguage(),
  fallbackLng: 'en',
  interpolation: {
    // React already escapes interpolated values.
    escapeValue: false,
  },
  returnNull: false,
});

export default i18n;
